import {
  useAdSettings,
  useAnalytics,
  useIdentify,
  useTrackEvent
} from "@outschool/ui-analytics";
import { useTokenContext } from "@outschool/ui-auth";
import { useTopNoticeContext } from "@outschool/ui-components-website";
import React from "react";

import { useGraphqlContext } from "../providers/GraphqlProvider";
import AppState from "./AppState";

interface AppStateProviderProps {
  appState: AppState;
}

const AppStateContext = React.createContext<{ appState: AppState } | null>(
  null
);

export function AppStateProvider({
  appState,
  children
}: React.PropsWithChildren<AppStateProviderProps>) {
  const adSettings = useAdSettings();
  const analytics = useAnalytics();
  const { client, resetClientVersion, logout } = useGraphqlContext();
  const identify = useIdentify();
  const { getTokens, getAuthData, setTokens } = useTokenContext();
  const { setTimedTopNotice, setTopNotice } = useTopNoticeContext();
  const trackEvent = useTrackEvent();

  React.useEffect(() => {
    appState.setAnalytics(analytics, identify, trackEvent, adSettings);
    appState.setLogoutCurrentUser(logout);
    appState.setResetClientVersion(resetClientVersion);
    appState.setApolloClient(client);
    appState.setTopNoticeSetter(setTopNotice);
    appState.setTimedTopNoticeSetter(setTimedTopNotice);
    appState.setAuth({
      getTokens,
      setTokens,
      getAuthData
    });
    appState.initUserData();
  }, [
    appState,
    analytics,
    identify,
    trackEvent,
    adSettings,
    logout,
    resetClientVersion,
    client,
    setTopNotice,
    setTimedTopNotice,
    getTokens,
    setTokens,
    getAuthData
  ]);

  const [appStateWrappedInNewObject, setAppStateWrappedInNewObject] =
    React.useState({ appState });

  const changeListener = React.useCallback(() => {
    setAppStateWrappedInNewObject({ appState });
  }, [appState]);

  React.useEffect(() => {
    if (appState) {
      appState.addChangeListener(changeListener);
      return () => {
        appState.removeChangeListener(changeListener);
      };
    } else {
      return () => {};
    }
  }, [appState, changeListener]);

  return (
    <AppStateContext.Provider value={appStateWrappedInNewObject}>
      {children}
    </AppStateContext.Provider>
  );
}

export function withAppState(DecoratedComponent: any) {
  const displayName = DecoratedComponent.displayName || "Component";

  // TODO: Use React.forwardRef
  return class AppStateWrapper extends React.Component {
    static displayName = `AppStateWrapper(${displayName})`;

    render() {
      return (
        <AppStateContext.Consumer>
          {appStateWrappedInNewObject => (
            <DecoratedComponent
              // @ts-ignore TS(2531): Object is possibly 'null'.
              appState={appStateWrappedInNewObject.appState}
              {...this.props}
            />
          )}
        </AppStateContext.Consumer>
      );
    }
  };
}

export function useAppState() {
  const appStateWrappedInNewObject = React.useContext(AppStateContext);
  // @ts-ignore TS(2531): Object is possibly 'null'.
  return appStateWrappedInNewObject.appState;
}
